/**
 * A page that stays open across a deploy still holds the old bundle, whose
 * lazily loaded pages have hashed names the new deploy no longer serves.
 * Cloudflare's single-page fallback answers those with index.html (200,
 * text/html), the module import fails, and vue-router drops the navigation
 * without a word: every link in the sidebar silently does nothing until the
 * person reloads. Seen on staging when a deploy landed under an open portal.
 *
 * The frontends reload into the page that was asked for. These two predicates
 * are the whole decision, kept here so they are tested; the reload itself is
 * `installStaleBundleReload()` in staleBundleReload.ts.
 */

/** Messages browsers give a dynamic import whose module could not be loaded. */
const STALE_CHUNK_MESSAGES = [
  'Failed to fetch dynamically imported module', // Chromium
  'error loading dynamically imported module', // Firefox
  'Importing a module script failed', // Safari
  'Unable to preload CSS', // Vite's own preload of a lazy page's stylesheet
]

export function isStaleChunkError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : typeof error === 'string' ? error : ''
  return STALE_CHUNK_MESSAGES.some(text => message.includes(text))
}

/**
 * At most one reload a minute. A chunk that is missing for some other reason
 * (a broken deploy, a blocked CDN) would otherwise reload the page for ever.
 */
export const STALE_BUNDLE_RELOAD_INTERVAL_MS = 60_000

export function shouldReloadForStaleBundle(lastReloadAt: number | null, now: number): boolean {
  return lastReloadAt === null || !Number.isFinite(lastReloadAt) || now - lastReloadAt >= STALE_BUNDLE_RELOAD_INTERVAL_MS
}
